/**
 * POP3D bootstrap.
 *
 * Thin router: picks a template from URL hash > localStorage > brand.template,
 * mounts it, swaps live when the user clicks a template tab.
 *
 * Industry palette + Lenis smooth scroll + AR modal are global concerns
 * applied here regardless of which template is active.
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import { brand, products } from './products.js';
import { look, industryPresets, industryCSS } from './look.js';
import { templates, DEFAULT_TEMPLATE } from './templates/registry.js';
import { el } from './core/utils.js';
import { mountAgency } from './agency.js';
import { resolveBrandSlug } from './brands/registry.js';
import { mountStoreChrome } from './store.js';
import { mountShop } from './shop.js';

gsap.registerPlugin(ScrollTrigger);

const canvas = document.getElementById('stage');
const host = document.getElementById('app');
const agencyTop = document.getElementById('agency-top');
const agencyBottom = document.getElementById('agency-bottom');

// Resolve which brand this URL is serving.
const route = resolveBrandSlug();
const IS_STORE = route.isStore;

// --- Brand theme: fonts + palette. Prefers brand.theme, falls back to industryCSS. ---
(function applyBrandTheme() {
  const r = document.documentElement.style;
  const t = brand.theme;
  if (t) {
    const map = {
      '--font-display': t.display, '--font-body': t.body,
      '--ink': t.ink, '--paper': t.paper,
      '--accent-1': t.a1, '--accent-2': t.a2, '--accent-3': t.a3,
    };
    for (const [k, v] of Object.entries(map)) if (v) r.setProperty(k, v);
  } else {
    const vars = industryCSS[brand.industry];
    if (vars) for (const [k, v] of Object.entries(vars)) r.setProperty(k, v);
  }
})();

// 3D look: base + industry preset + per-brand override (brand.look wins).
const industryLook = industryPresets[brand.industry] || {};
const mergedLook = { ...look, ...industryLook, ...(brand.look || {}) };

// --- Lenis smooth scroll, shared across templates ---
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let lenis = null;
if (!reduceMotion) {
  lenis = new Lenis({
    duration: 1.05,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    smoothTouch: false,
  });
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);
}

// --- Template lifecycle ---
let active = null;
let activeId = null;
let pauseIO = null;

const TPL_KEY = 'pop3d:template:' + route.slug;   // per-brand template memory

function getInitialTemplate() {
  const hash = window.location.hash.replace('#', '');
  if (hash && templates[hash]) return hash;                 // explicit deep-link wins
  // A storefront opens in the template its brand was designed for.
  if (IS_STORE && brand.template && templates[brand.template]) return brand.template;
  const stored = localStorage.getItem(TPL_KEY);
  if (stored && templates[stored]) return stored;
  if (brand.template && templates[brand.template]) return brand.template;
  return DEFAULT_TEMPLATE;
}

function refreshCanvasPauseObserver() {
  pauseIO?.disconnect();
  pauseIO = null;
  const candidates = [
    host.querySelector('.hero'),
    host.querySelector('#collection'),
    host.querySelector('.editorial-stage'),
    host.querySelector('.editorial-intro'),
    host.querySelector('.editorial-outro'),
  ].filter(Boolean);
  if (!candidates.length) return;
  let visible = 0;
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting) visible++; else visible = Math.max(0, visible - 1);
    }
    active?.setPaused?.(visible === 0);
  }, { threshold: 0 });
  candidates.forEach((t) => io.observe(t));
  pauseIO = io;
}

function mountTemplate(id) {
  if (activeId === id) return;
  if (active) {
    try { active.destroy(); } catch (e) { console.warn('[main] destroy failed', e); }
    active = null;
  }
  // Reset scroll to top so the new template's ScrollTrigger starts at progress 0.
  window.scrollTo(0, 0);
  if (lenis) lenis.scrollTo(0, { immediate: true });
  ScrollTrigger.getAll().forEach((s) => s.kill()); // safety: nuke any orphans

  const T = templates[id] || templates[DEFAULT_TEMPLATE];
  active = T.mount(canvas, products, mergedLook, host, { embedded: true });
  activeId = id;
  localStorage.setItem(TPL_KEY, id);
  history.replaceState(null, '', `#${id}`);

  document.querySelectorAll('.tpl-tab').forEach((b) => {
    b.classList.toggle('is-active', b.dataset.template === id);
  });

  requestAnimationFrame(() => {
    ScrollTrigger.refresh();
    refreshCanvasPauseObserver();
  });
}

// --- Tab switcher UI (agency demo only) ---
function buildTabs() {
  const tabs = el('nav', { className: 'tpl-tabs', 'aria-label': 'Template switcher' });
  for (const t of Object.values(templates)) {
    const btn = el('button', {
      type: 'button',
      className: 'tpl-tab',
      dataset: { template: t.id },
      title: t.description,
    }, t.label);
    btn.addEventListener('click', () => mountTemplate(t.id));
    tabs.appendChild(btn);
  }
  document.body.appendChild(tabs);
}

// --- AR modal (shared across templates) ---
let arViewerLoaded = false;
let arModal = null;

async function ensureModelViewer() {
  if (arViewerLoaded) return;
  arViewerLoaded = true;
  await import(/* @vite-ignore */ 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js');
}

function openAR(modelUrl, bg) {
  if (!arModal) {
    arModal = document.createElement('div');
    arModal.className = 'ar-modal';
    arModal.addEventListener('click', (e) => {
      if (e.target === arModal || e.target.dataset.close === 'true') closeAR();
    });
    document.body.appendChild(arModal);
  }
  arModal.replaceChildren();
  const close = document.createElement('button');
  close.className = 'ar-close';
  close.dataset.close = 'true';
  close.textContent = '✕';
  arModal.appendChild(close);
  const viewer = document.createElement('model-viewer');
  viewer.setAttribute('src', modelUrl);
  viewer.setAttribute('ar', '');
  viewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
  viewer.setAttribute('camera-controls', '');
  viewer.setAttribute('auto-rotate', '');
  viewer.setAttribute('shadow-intensity', '1');
  viewer.setAttribute('exposure', '1');
  if (bg) viewer.style.backgroundColor = bg;
  arModal.appendChild(viewer);
  arModal.classList.add('is-open');
}
function closeAR() { if (arModal) arModal.classList.remove('is-open'); }

document.body.addEventListener('click', async (e) => {
  const btn = e.target.closest?.('[data-ar]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  const card = btn.closest('.product-card');
  const modelUrl = btn.dataset.model;
  const bg = card?.style.background;
  await ensureModelViewer();
  openAR(modelUrl, bg);
});
window.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAR(); });

document.addEventListener('visibilitychange', () => active?.setPaused?.(document.hidden));

// --- Boot ---
if (IS_STORE) {
  // Storefront: photoreal photo grid + tap-to-3D/AR. No Three.js scene, no tabs.
  if (canvas) canvas.style.display = 'none';
  mountStoreChrome(agencyTop, agencyBottom, brand);
  active = mountShop(host, brand, products);
} else {
  // Agency landing: the marketing wrapper + the live Three.js template demo.
  mountAgency(agencyTop, agencyBottom);
  buildTabs();
  mountTemplate(getInitialTemplate());
}
